import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

export default function About() {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen pt-32 pb-24 bg-[#0A0A0A]" data-testid="about-page">
      <SEO 
        title={language === 'sr' ? 'O nama' : 'About Us'}
        description={language === 'sr' 
          ? 'Upoznajte braću Kovačić - Miloša i Ivana, osnivače Destilerije Kovačić. Od 2021. godine stvaramo vrhunske kraft džinove iz Mokrina.'
          : 'Meet the Kovačić brothers - Miloš and Ivan, founders of Destilerija Kovačić. Since 2021, we create premium craft gins from Mokrin.'
        }
        image="https://customer-assets.emergentagent.com/job_kovacic-shop/artifacts/eerrwdl4_Braca-min-scaled.jpg"
      />
      {/* Hero */}
      <section className="px-6 lg:px-12 mb-24">
        <div className="max-w-4xl mx-auto text-center">
          <div className="decorative-line mx-auto mb-8" />
          <p className="text-gold text-xs tracking-[0.4em] uppercase mb-4">
            {t('about_subtitle')}
          </p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-8">
            {t('about_title')}
          </h1>
        </div>
      </section>

      {/* Main Story Section with Brothers Image */}
      <section className="px-6 lg:px-12 mb-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_kovacic-shop/artifacts/eerrwdl4_Braca-min-scaled.jpg"
                  alt="Miloš i Ivan Kovačić"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -inset-4 border border-gold/20 -z-10" />
            </div>
            <div>
              <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">
                {language === 'sr' ? 'Braća Kovačić' : 'The Kovačić Brothers'}
              </h2>
              <p className="text-white/60 leading-relaxed mb-6">
                {language === 'sr' 
                  ? 'Destilerija Kovačić priča je o porodičnoj strasti i viziji, koju su 2021. godine pokrenuli dva brata — Miloš i Ivan Kovačić iz Mokrina.'
                  : 'Destilerija Kovačić is a story of family passion and vision, started in 2021 by two brothers — Miloš and Ivan Kovačić from Mokrin.'}
              </p>
              <p className="text-white/60 leading-relaxed mb-6">
                {language === 'sr' 
                  ? 'Njihova posvećenost kvalitetu i želja za stvaranjem jedinstvenih pića rezultirali su vrhunskim kraft džinovima koji osvajaju ljubitelje širom Srbije.'
                  : 'Their dedication to quality and desire to create unique drinks resulted in premium craft gins that are winning over enthusiasts across Serbia.'}
              </p>
              <p className="text-white/60 leading-relaxed mb-6">
                {language === 'sr' 
                  ? 'Svaka boca je rezultat pažljivo biranih biljaka i začina, sa ciljem da svaki gutljaj bude vrhunski doživljaj.'
                  : 'Every bottle is the result of carefully selected botanicals and spices, with the goal that every sip is a premium experience.'}
              </p>
              <p className="text-white/60 leading-relaxed">
                {language === 'sr' 
                  ? 'Miloš i Ivan lično nadgledaju svaki korak proizvodnje, stavljajući autentičnost i kvalitet na prvo mesto.'
                  : 'Miloš and Ivan personally oversee every step of production, putting authenticity and quality first.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="px-6 lg:px-12 py-24 bg-surface">
        <div className="max-w-4xl mx-auto text-center">
          <div className="decorative-line mx-auto mb-8" />
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">
            {language === 'sr' ? 'Više od destilerije' : 'More than a distillery'}
          </h2>
          <p className="text-white/60 text-lg leading-relaxed mb-6">
            {language === 'sr' 
              ? 'Destilerija Kovačić nije samo mesto gde nastaju džinovi. To je priča o porodici, inovaciji i strasti prema stvaranju vrhunskih pića.'
              : 'Destilerija Kovačić is not just a place where gins are made. It is a story of family, innovation and passion for creating premium drinks.'}
          </p>
          <p className="text-white/60 text-lg leading-relaxed mb-6">
            {language === 'sr' 
              ? 'Bilo da uživate u našim proizvodima u restoranima, barovima ili kod kuće, svaki naš džin donosi trenutke vredne pamćenja.'
              : 'Whether you enjoy our products in restaurants, bars or at home, each of our gins brings moments worth remembering.'}
          </p>
          <p className="text-gold text-xl font-heading italic">
            {language === 'sr' 
              ? 'Dobrodošli u svet džinova Destilerije Kovačić — od 2021. godine, stvarano s ljubavlju za sve istinske ljubitelje džina.'
              : 'Welcome to the world of Destilerija Kovačić gins — since 2021, created with love for all true gin lovers.'}
          </p>
        </div>
      </section>

      {/* Process Section */}
      <section className="px-6 lg:px-12 py-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="decorative-line mb-8" />
              <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">
                {language === 'sr' ? 'Naš Proces' : 'Our Process'}
              </h2>
              <p className="text-white/60 leading-relaxed mb-6">
                {language === 'sr' 
                  ? 'Naša destilerija veruje u male serije, kontrolisan kvalitet i stvaranje pića sa dušom. Svaki džin prolazi kroz pažljiv proces destilacije koji garantuje konzistentan i vrhunski ukus.'
                  : 'Our distillery believes in small batches, controlled quality and creating drinks with soul. Each gin goes through a careful distillation process that guarantees consistent and premium taste.'}
              </p>
              <p className="text-white/60 leading-relaxed mb-6">
                {language === 'sr' 
                  ? 'Od samog početka, Destilerija Kovačić vodi se idejom stvaranja džinova koji se izdvajaju autentičnošću i vrhunskim ukusom.'
                  : 'From the very beginning, Destilerija Kovačić has been guided by the idea of creating gins that stand out with authenticity and premium taste.'}
              </p>
              <p className="text-white/60 leading-relaxed">
                {language === 'sr' 
                  ? 'Koristimo samo najkvalitetnije botaničke sastojke, pažljivo birane kako bi svaki naš proizvod bio jedinstven doživljaj.'
                  : 'We use only the highest quality botanical ingredients, carefully selected so that each of our products is a unique experience.'}
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_kovacic-shop/artifacts/nv5n84i5_Feature-image-blog.webp"
                  alt="Craft process"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -inset-4 border border-gold/20 -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Three Gins Section */}
      <section className="px-6 lg:px-12 py-24 bg-surface">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="decorative-line mx-auto mb-8" />
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
              {language === 'sr'
                ? 'Tri autentična džina'
                : 'Three authentic gins'}
            </h2>
            <p className="text-white/50">
              {language === 'sr'
                ? 'Za svaki ukus i trenutak'
                : 'For every taste and moment'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="glass p-8 text-center elegant-border">
              <h3 className="font-heading text-2xl text-gold mb-4">ОРИЏИН Classic</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                {language === 'sr'
                  ? 'Herbalni džin bogatog i sofisticiranog ukusa, idealan za ljubitelje klasičnih pića i džin & tonika.'
                  : 'A rich and sophisticated herbal gin, ideal for lovers of classic drinks and gin & tonic.'}
              </p>
            </div>
            <div className="glass p-8 text-center elegant-border">
              <h3 className="font-heading text-2xl text-gold mb-4">ОРИЏИН Citrus</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                {language === 'sr'
                  ? 'Osvežavajući džin citrusnih nota, savršen za koktele i letnje večeri.'
                  : 'Refreshing citrus gin, perfect for cocktails and summer evenings.'}
              </p>
            </div>
            <div className="glass p-8 text-center elegant-border">
              <h3 className="font-heading text-2xl text-gold mb-4">ОРИЏИН UV</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                {language === 'sr'
                  ? 'Naš najatraktivniji džin, prepoznatljiv po safirno plavoj boji koja se transformiše u ljubičastu. ОРИЏИН UV nije samo piće — to je vizuelno i ukusno iskustvo.'
                  : 'Our most attractive gin, recognizable by its sapphire blue color that transforms into purple. ORIGIN UV is not just a drink — it is a visual and taste experience.'}
              </p>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/shop"
              className="inline-flex items-center gap-3 border border-gold text-gold hover:bg-gold hover:text-black px-10 py-4 text-sm tracking-[0.15em] uppercase transition-all duration-300"
            >
              {language === 'sr' ? 'Pogledaj kolekciju' : 'View collection'}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
